import { FC, useState, useEffect, useMemo } from 'react'
import { FormElement, FormElementType, Variable, MobileListItem, ContextMenuAction, SwipeAction } from '@mobileTypes'
import { ScriptValueDialog, variablesToFieldsInfo } from '@microservices/wiskey-react-components'
import { Code as CodeIcon } from '@mui/icons-material'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import { EventFlowsForElementModal } from '@mobileComponents/EventFlow'
import type { EventFlowElementRef } from '@mobileComponents/EventFlow'
import type { MobileEventFlow } from '@mobileTypes'
import { EmbeddedListConfigDialog } from './EmbeddedListConfigDialog'
import {
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material'

import { useFetchMultipleObjectsByCodeQuery } from '@redux/api/objects.api'
import { FIELD_VALUE_TYPE, FORMATS_DATE_OPTIONS, TYPES_SOURCE_OPTIONS } from '@constants'
import { ObjectFieldDTO } from '@types'
import { useMobileGlobalVariables } from '@mobileHooks'
import { buildFormScriptFieldsInfo, buildFormScriptVariables } from '@mobileConstants'
import { VariableAutocompleteWithCreate } from '@mobileComponents/Variables'
import { InternalIdInput } from '@library'
import {
  ModalRefPathPicker,
  type RefPathValue,
} from '@libraryComponents/ModalRefPathPicker/ModalRefPathPicker'

export interface ElementConfig {
  type: FormElementType
  id?: string // For BUTTON and SWIPE (legacy)
  internalId?: string // User-defined stable ID for references and event flows
  title?: string
  titleJs?: string // JS expression for dynamic title
  useTitleJs?: boolean // Whether to use JS for title
  textAlign?: 'LEFT' | 'CENTER' | 'RIGHT'
  readOnly?: boolean
  // For FIELD
  fieldCode?: string
  objectCode?: string
  variableName?: string // Selected variable name
  variableObjectCode?: string // Object code from variable (if object type)
  placeholder?: string
  fieldType?: 'single' | 'enum' | 'date' | 'datetime' | 'number' | 'boolean'
  required?: boolean
  multiline?: boolean
  bindType?: 'field' | 'js'
  valueJs?: string // JS expression for field value
  valueType?: string // Type of field value
  formatDate?: string // Date format
  pathArray?: RefPathValue['pathArray'] | null
  // For BUTTON
  commandName?: string
  commandObjectCode?: string
  commandHttpMethod?: string
  commandResource?: TYPES_SOURCE_OPTIONS
  /** BUTTON: background color (e.g. #3d8761) */
  buttonColor?: string
  // For BUTTON/SWIPE: multiple actions
  actions?: SwipeAction[]
  chooseActionJs?: string
  // For SWIPE
  backgroundColor?: string
  // For all: condition to show element
  visibleIfJs?: string
  // For FIELD bindType js
  valuePath?: string
  // For EMBEDDED_LIST
  embeddedListItemId?: string
  embeddedListConfig?: Partial<MobileListItem>
  selectedIdsVariable?: string
  showCheckboxes?: boolean
  itemIdField?: string
  contextMenuActions?: ContextMenuAction[]
  listHeader?: string
  listSubheader?: string
  listSubheaderJs?: string
  selectAllEnabled?: boolean
  selectAllText?: string
  variableBindings?: Record<string, string>
  readOnlyIfJs?: string
  // For ACCORDION
  accordionTitle?: string
  accordionStyle?: { backgroundColor?: string; borderRadius?: number; padding?: number; marginBottom?: number }
  accordionChildren?: FormElement[]
  // For FILE
  fileTypes?: string[]
  fileVariableName?: string
}

interface ElementConfigDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  elementType?: FormElementType
  initialData?: Partial<FormElement>
  variables?: Variable[] // Local form variables
  formObjectCode?: string // Parent form objectCode (fallback for FIELD objectCode)
  onClose: () => void
  onSave: (config: ElementConfig) => void
  onCreateVariable?: (name: string, isGlobal: boolean, valueType?: string, variableType?: string) => void
  /** When provided (edit mode + element with id), shows "View event flows" for this element */
  eventFlows?: MobileEventFlow[]
  onEventFlowsChange?: (flows: MobileEventFlow[]) => void
  elementRef?: EventFlowElementRef
  formElements?: any[] // All form elements for event flow modal (button/swipe/field selection)
}

export const ElementConfigDialog: FC<ElementConfigDialogProps> = ({
  open,
  mode,
  elementType,
  initialData,
  variables = [],
  formObjectCode,
  onClose,
  onSave,
  onCreateVariable,
  eventFlows = [],
  onEventFlowsChange,
  elementRef,
  formElements = [],
}) => {
  const [showTitleJsEditor, setShowTitleJsEditor] = useState(false)
  const [showValueJsEditor, setShowValueJsEditor] = useState(false)
  const [showVisibleIfJsEditor, setShowVisibleIfJsEditor] = useState(false)
  const [showEmbeddedListConfig, setShowEmbeddedListConfig] = useState(false)
  const [eventFlowsModalOpen, setEventFlowsModalOpen] = useState(false)
  const showEventFlows = mode === 'edit' && elementRef && onEventFlowsChange

  // Fetch global variables
  const { variables: globalVariables } = useMobileGlobalVariables()

  // Combine local and global variables
  const allVariables = useMemo(() => {
    const local = variables.map(v => ({ ...v, isGlobal: false }))
    const global = globalVariables.map(v => ({ ...v, isGlobal: true }))
    return [...local, ...global]
  }, [variables, globalVariables])

  // Variable hints for ScriptValueDialog (JS Title, visibleIfJs, chooseActionJs): formData, item, localVariables, globalVariables, common
  const titleJsScriptVariables = useMemo(
    () => buildFormScriptVariables(variables, globalVariables),
    [variables, globalVariables]
  )
  const titleJsScriptFieldsInfo = useMemo(
    () => buildFormScriptFieldsInfo(variables, globalVariables),
    [variables, globalVariables]
  )

  const valueJsScriptVariables = useMemo(
    () => buildFormScriptVariables(variables, globalVariables).filter(v => v.name !== 'item'),
    [variables, globalVariables]
  )

  // Auto-determine fieldType based on valueType
  const getFieldType = (valueType?: string): 'single' | 'enum' | 'date' | 'datetime' | 'number' | 'boolean' => {
    switch (valueType) {
      case 'number':
      case 'integer':
      case 'long':
      case 'double':
      case 'float':
        return 'number'
      case 'date':
        return 'date'
      case 'datetime':
      case 'timestamp':
        return 'datetime'
      case 'enum':
        return 'enum'
      case 'boolean':
        return 'boolean'
      default:
        return 'single'
    }
  }

  const initialCommandResource: TYPES_SOURCE_OPTIONS | undefined =
    (initialData?.command?.resource as TYPES_SOURCE_OPTIONS | undefined) ?? TYPES_SOURCE_OPTIONS.OBJECT

  const [config, setConfig] = useState<ElementConfig>(() => ({
    type: elementType || initialData?.type || 'FIELD',
    id: initialData?.id || '',
    internalId: initialData?.internalId ?? '',
    title: initialData?.title || '',
    titleJs: initialData?.titleJs || '',
    useTitleJs: !!(initialData?.titleJs),
    textAlign: initialData?.textAlign || 'LEFT',
    readOnly: initialData?.editable === false,
    fieldCode: initialData?.data?.code || '',
    objectCode: initialData?.data?.objectCode || formObjectCode || '',
    variableName: initialData?.data?.variableName || '',
    variableObjectCode: initialData?.data?.variableObjectCode || '',
    placeholder: initialData?.data?.placeholderValue || '',
    fieldType: getFieldType(initialData?.data?.type),
    required: initialData?.data?.userRequired || initialData?.data?.defaultRequired || false,
    multiline: initialData?.data?.isMultiline || false,
    bindType: (initialData?.data?.bindType as 'field' | 'js') || 'field',
    valueJs: initialData?.data?.bindType === 'js' ? (initialData?.data?.value || '') : '',
    valueType: initialData?.data?.valueType || 'string',
    formatDate: initialData?.data?.formatDate || undefined,
    pathArray: (initialData?.data as any)?.pathArray || null,
    commandName: initialData?.command?.name || '',
    commandObjectCode: initialData?.command?.objectCode || '',
    commandHttpMethod: initialData?.command?.httpMethod || 'POST',
    commandResource: initialCommandResource,
    buttonColor: initialData?.buttonColor,
    // For EMBEDDED_LIST
    embeddedListItemId: initialData?.embeddedListItemId,
    embeddedListConfig: initialData?.embeddedListConfig,
    selectedIdsVariable: initialData?.selectedIdsVariable,
    showCheckboxes: initialData?.showCheckboxes !== undefined ? initialData.showCheckboxes : true,
    itemIdField: initialData?.itemIdField || '_id',
    contextMenuActions: initialData?.contextMenuActions,
    actions: initialData?.actions,
    chooseActionJs: initialData?.chooseActionJs,
    backgroundColor: initialData?.backgroundColor,
    visibleIfJs: initialData?.visibleIfJs,
    valuePath: (initialData?.data as any)?.valuePath,
    listHeader: initialData?.listHeader,
    listSubheader: initialData?.listSubheader,
    listSubheaderJs: initialData?.listSubheaderJs,
    selectAllEnabled: initialData?.selectAllEnabled,
    selectAllText: initialData?.selectAllText,
    variableBindings: initialData?.variableBindings,
    readOnlyIfJs: initialData?.readOnlyIfJs,
    accordionTitle: (initialData as any)?.title,
    accordionStyle: (initialData as any)?.style,
    accordionChildren: initialData?.accordionChildren || [],
    fileTypes: (initialData as any)?.fileTypes || [],
    fileVariableName: (initialData as any)?.fileVariableName || '',
  }))

  // Get selected variable (variableName stores variable's internalId)
  const selectedVariable = useMemo(() => {
    return allVariables.find(v => v.internalId === config.variableName)
  }, [allVariables, config.variableName])

  const displayPath = useMemo(() => {
    if (!config.pathArray || config.pathArray.length === 0) return ''
    return config.pathArray
      .map(item => item?.field)
      .filter(Boolean)
      .join('.')
  }, [config.pathArray])

  const variableObjectCodes = config.variableObjectCode ? [config.variableObjectCode] : []
  const { data: variableObjectsData, isLoading: isLoadingVariableFields } =
    useFetchMultipleObjectsByCodeQuery(variableObjectCodes, {
      skip:
        !config.variableObjectCode ||
        config.type !== 'FIELD' ||
        selectedVariable?.valueType !== 'object',
    })

  const formObjectCodes = config.objectCode ? [config.objectCode] : []
  const { data: formObjectsData, isLoading: isLoadingFormFields } =
    useFetchMultipleObjectsByCodeQuery(formObjectCodes, {
      skip: !config.objectCode || config.type !== 'FIELD' || config.bindType !== 'field',
    })

  const isLoadingFields = isLoadingVariableFields || isLoadingFormFields

  const fieldOptions = useMemo<ObjectFieldDTO[]>(() => {
    if (config.variableObjectCode && variableObjectsData) {
      const obj = variableObjectsData.find(o => o.code === config.variableObjectCode)
      return obj?.fields || []
    }

    if (config.objectCode && formObjectsData) {
      const obj = formObjectsData.find(o => o.code === config.objectCode)
      return obj?.fields || []
    }

    return []
  }, [config.variableObjectCode, variableObjectsData, config.objectCode, formObjectsData])

  const selectedField = useMemo(
    () => fieldOptions.find(f => f.name === config.fieldCode),
    [fieldOptions, config.fieldCode],
  )

  const displayObjectOption = useMemo(() => {
    const code = selectedField?.model || config.variableObjectCode || selectedVariable?.objectCode
    return code ? { id: code, label: code } : null
  }, [selectedField?.model, config.variableObjectCode, selectedVariable?.objectCode])

  // Update config when dialog opens with new data
  useEffect(() => {
    if (open) {
      const effectCommandResource: TYPES_SOURCE_OPTIONS | undefined =
        (initialData?.command?.resource as TYPES_SOURCE_OPTIONS | undefined) ?? TYPES_SOURCE_OPTIONS.OBJECT

      setConfig({
        type: elementType || initialData?.type || 'FIELD',
        id: initialData?.id || '',
        internalId: initialData?.internalId ?? '',
        title: initialData?.title || '',
        titleJs: initialData?.titleJs || '',
        useTitleJs: !!(initialData?.titleJs),
        textAlign: initialData?.textAlign || 'LEFT',
        readOnly: initialData?.editable === false,
        fieldCode: initialData?.data?.code || '',
        objectCode: initialData?.data?.objectCode || formObjectCode || '',
        variableName: initialData?.data?.variableName || '',
        variableObjectCode: initialData?.data?.variableObjectCode || '',
        placeholder: initialData?.data?.placeholderValue || '',
        fieldType: getFieldType(initialData?.data?.type),
        required: initialData?.data?.userRequired || initialData?.data?.defaultRequired || false,
        multiline: initialData?.data?.isMultiline || false,
        bindType: (initialData?.data?.bindType as 'field' | 'js') || 'field',
        valueJs: initialData?.data?.bindType === 'js' ? (initialData?.data?.value || '') : '',
        valueType: initialData?.data?.valueType || 'string',
        formatDate: initialData?.data?.formatDate || undefined,
        pathArray: (initialData?.data as any)?.pathArray || null,
        commandName: initialData?.command?.name || '',
        commandObjectCode: initialData?.command?.objectCode || '',
        commandHttpMethod: initialData?.command?.httpMethod || 'POST',
        commandResource: effectCommandResource,
        buttonColor: initialData?.buttonColor,
        actions: initialData?.actions,
        chooseActionJs: initialData?.chooseActionJs,
        backgroundColor: initialData?.backgroundColor,
        visibleIfJs: initialData?.visibleIfJs,
        valuePath: (initialData?.data as any)?.valuePath,
        listHeader: initialData?.listHeader,
        listSubheader: initialData?.listSubheader,
        listSubheaderJs: initialData?.listSubheaderJs,
        selectAllEnabled: initialData?.selectAllEnabled,
        selectAllText: initialData?.selectAllText,
        variableBindings: initialData?.variableBindings,
        readOnlyIfJs: initialData?.readOnlyIfJs,
        accordionTitle: (initialData as any)?.title,
        accordionStyle: (initialData as any)?.style,
        accordionChildren: initialData?.accordionChildren || [],
        fileTypes: (initialData as any)?.fileTypes || [],
        fileVariableName: (initialData as any)?.fileVariableName || '',
      })
    }
  }, [open, elementType, initialData])

  const handleChange = (field: keyof ElementConfig, value: any) => {
    // Если выбран JavaScript, очищаем связанные с переменными поля
    if (field === 'bindType' && value === 'js') {
      setConfig(prev => ({
        ...prev,
        bindType: 'js',
        variableName: '',
        variableObjectCode: '',
        fieldCode: '',
        objectCode: '',
      }))
      return
    }

    setConfig(prev => ({ ...prev, [field]: value }))
  }

  const handleSave = () => {
    // Гарантируем, что при JS биндинге переменные не сохраняются
    const sanitizedConfig =
      config.bindType === 'js'
        ? { ...config, variableName: '', variableObjectCode: '' }
        : config

    onSave(sanitizedConfig)
  }

  const handleSaveValueJs = (code: string) => {
    handleChange('valueJs', code)
    setShowValueJsEditor(false)
  }

  const renderFieldsForType = () => {
    switch (config.type) {
      case 'FIELD':
        return (
          <>
            {/* Field Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Field Title"
                value={config.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
              />
            </Grid>

            {/* Placeholder */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Placeholder"
                value={config.placeholder}
                onChange={(e) => handleChange('placeholder', e.target.value)}
              />
            </Grid>

            {/* Bind Type Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Bind Type</InputLabel>
                <Select
                  value={config.bindType || 'field'}
                  onChange={(e) => handleChange('bindType', e.target.value)}
                  label="Bind Type"
                >
                  <MenuItem value="field">Field</MenuItem>
                  <MenuItem value="js">JavaScript</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Variable Selection */}
            {config.bindType === 'field' && <Grid item xs={12}>
              <Autocomplete
                options={allVariables}
                groupBy={(option) => option.isGlobal ? 'Global Variables' : 'Local Variables'}
                getOptionLabel={(option) => {
                  const suffix = option.valueType === 'object' && option.objectCode
                    ? ` (${option.objectCode})`
                    : ` (${option.valueType})`
                  return `${option.internalId}${suffix}`
                }}
                value={allVariables.find(v => v.internalId === config.variableName) || null}
                onChange={(_, newValue) => {
                  handleChange('variableName', newValue?.internalId || '')
                  handleChange('variableObjectCode', newValue?.objectCode || '')
                  handleChange('valueType', newValue?.valueType || 'string')
                  handleChange('objectCode', newValue?.objectCode || '')
                  // Clear field selection when variable changes
                  handleChange('fieldCode', '')
                  handleChange('title', '')
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Variable"
                    required
                    helperText={
                      'Select a variable to bind this field'
                    }
                  />
                )}
              />
            </Grid>}

            {/* Field Selection - only for object-type variables with bindType = field */}
            {config.bindType === 'field' && selectedVariable?.valueType === 'object' && (
              <Grid item xs={12}>
                <Autocomplete
                  options={fieldOptions}
                  getOptionLabel={(option) => `${option.name}`}
                  value={fieldOptions.find(f => f.name === config.fieldCode) || null}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      handleChange('fieldCode', newValue.name)
                      handleChange('valueType', newValue.valueType || 'string')
                      handleChange('fieldType', getFieldType(newValue.valueType))
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Field"
                      required
                      helperText="Select field from object"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingFields && <CircularProgress size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  loading={isLoadingFields}
                  disabled={!config.variableObjectCode}
                />
              </Grid>
            )}

            {/* Display Value (path) - only for object link types */}
            {config.bindType === 'field' &&
              (config.valueType === FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK ||
                config.valueType === FIELD_VALUE_TYPE.OBJ_PK_LINK) &&
              displayObjectOption && (
                <Grid item xs={12}>
                  <ModalRefPathPicker
                    label="Display Value"
                    value={
                      config.pathArray
                        ? {
                          path: displayPath,
                          pathArray: config.pathArray || [],
                        }
                        : undefined
                    }
                    onChange={next => handleChange('pathArray', next?.pathArray || null)}
                    hasClearInInput
                    isEdit={!!config.pathArray?.length}
                    refPickAllowed
                    object={displayObjectOption}
                    objectOptions={[displayObjectOption]}
                  />
                </Grid>
              )}

            {/* Display Variable Type (read-only info) */}
            {config.variableName && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Variable Type"
                  value={selectedVariable?.valueType || 'N/A'}
                  disabled
                  helperText="Type of the selected variable"
                />
              </Grid>
            )}

            {config.bindType === 'field' && <Grid item xs={6}>
              <TextField
                fullWidth
                label="Value Type"
                value={config.valueType || 'string'}
                disabled
                helperText="Auto-determined from selected field"
              />
            </Grid>}

            {/* Info message for non-object variables */}
            {config.bindType === 'field' && config.variableName && selectedVariable?.valueType !== 'object' && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Selected variable is not an object type. Field will bind directly to the variable value.
                </Typography>
              </Grid>
            )}

            {/* JavaScript Value (only for bindType = js) */}
            {config.bindType === 'js' && (
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setShowValueJsEditor(true)}
                  startIcon={<CodeIcon />}
                >
                  {config.valueJs ? 'Edit JavaScript Expression' : 'Add JavaScript Expression'}
                </Button>
                {config.valueJs && (
                  <Box p={1} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1, mb: 1 }}>
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: 11,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        margin: 0,
                      }}
                    >
                      {config.valueJs.length > 100
                        ? config.valueJs.substring(0, 100) + '...'
                        : config.valueJs}
                    </Typography>
                  </Box>
                )}
              </Grid>
            )}

            {/* Value Type - auto-determined from selected field */}


            {/* Format Date (only for date/datetime types) */}
            {config.bindType === 'field' && (config.valueType === 'date' || config.valueType === 'datetime') && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Date Format</InputLabel>
                  <Select
                    value={config.formatDate || ''}
                    onChange={(e) => handleChange('formatDate', e.target.value)}
                    label="Date Format"
                  >
                    <MenuItem value="">Default</MenuItem>
                    {FORMATS_DATE_OPTIONS.map((format) => (
                      <MenuItem key={format.id} value={format.id}>
                        {format.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Text Align</InputLabel>
                <Select
                  value={config.textAlign}
                  onChange={(e) => handleChange('textAlign', e.target.value as any)}
                  label="Text Align"
                >
                  <MenuItem value="LEFT">Left</MenuItem>
                  <MenuItem value="CENTER">Center</MenuItem>
                  <MenuItem value="RIGHT">Right</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.required}
                    onChange={(e) => handleChange('required', e.target.checked)}
                  />
                }
                label="Required"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.multiline}
                    onChange={(e) => handleChange('multiline', e.target.checked)}
                  />
                }
                label="Multiline"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.readOnly}
                    onChange={(e) => handleChange('readOnly', e.target.checked)}
                  />
                }
                label="Read-only"
              />
            </Grid>
          </>
        )

      case 'TITLE':
        return (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title Text"
                value={config.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Text Align</InputLabel>
                <Select
                  value={config.textAlign}
                  onChange={(e) => handleChange('textAlign', e.target.value as any)}
                  label="Text Align"
                >
                  <MenuItem value="LEFT">Left</MenuItem>
                  <MenuItem value="CENTER">Center</MenuItem>
                  <MenuItem value="RIGHT">Right</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )

      case 'BUTTON':
        return (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Button ID"
                value={config.id}
                onChange={(e) => handleChange('id', e.target.value)}
                required
                helperText="Unique identifier for this button (used in Actions)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.useTitleJs || false}
                    onChange={(e) => handleChange('useTitleJs', e.target.checked)}
                  />
                }
                label="JS Title"
              />
            </Grid>
            {!config.useTitleJs ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Button Title"
                  value={config.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  helperText="Static title text"
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title JS"
                  value={config.titleJs}
                  helperText="JavaScript expression for dynamic title"
                  InputProps={{
                    onClick: () => setShowTitleJsEditor(true),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                        >
                          <CodeIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Text Align</InputLabel>
                <Select
                  value={config.textAlign}
                  onChange={(e) => handleChange('textAlign', e.target.value as any)}
                  label="Text Align"
                >
                  <MenuItem value="LEFT">Left</MenuItem>
                  <MenuItem value="CENTER">Center</MenuItem>
                  <MenuItem value="RIGHT">Right</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Button color"
                value={config.buttonColor || ''}
                onChange={(e) => handleChange('buttonColor', e.target.value)}
                placeholder="#3d8761"
                helperText="e.g. #3d8761 or leave empty for default"
              />
            </Grid>
          </>
        )

      case 'SWIPE':
        return (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Swipe ID"
                value={config.id}
                onChange={(e) => handleChange('id', e.target.value)}
                required
                helperText="Unique identifier for this swipe element (used in Actions)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.useTitleJs || false}
                    onChange={(e) => handleChange('useTitleJs', e.target.checked)}
                  />
                }
                label="JS Title"
              />
            </Grid>
            {!config.useTitleJs ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Swipe Title"
                  value={config.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  helperText="Static title text"
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title JS"
                  value={config.titleJs}
                  helperText="JavaScript expression for dynamic title"
                  InputProps={{
                    onClick: () => setShowTitleJsEditor(true),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                        >
                          <CodeIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth label="Background color" value={config.backgroundColor || ''} onChange={(e) => handleChange('backgroundColor', e.target.value)} placeholder="#e3f2fd" helperText="e.g. #e3f2fd or leave empty for auto" />
            </Grid>
          </>
        )

      case 'EMBEDDED_LIST':
        return (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Element ID"
                value={config.id || ''}
                onChange={(e) => handleChange('id', e.target.value)}
                helperText="Unique identifier for this embedded list (used in Actions)"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Configure embedded list settings
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setShowEmbeddedListConfig(true)}
                  sx={{ mt: 1 }}
                >
                  {config.embeddedListItemId || config.embeddedListConfig
                    ? 'Edit List Configuration'
                    : 'Configure List'}
                </Button>
                {config.embeddedListItemId && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Using list: {config.embeddedListItemId}
                  </Typography>
                )}
              </Box>
            </Grid>
          </>
        )

      case 'ACCORDION':
        return (
          <>
            <Grid item xs={12}>
              <TextField fullWidth label="Accordion title" value={config.accordionTitle || ''} onChange={(e) => handleChange('accordionTitle', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Background color" value={config.accordionStyle?.backgroundColor || ''} onChange={(e) => handleChange('accordionStyle', { ...config.accordionStyle, backgroundColor: e.target.value })} placeholder="#E9ECEC" />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Children: edit via JSON or add elements in Config tab (accordionChildren)</Typography>
            </Grid>
          </>
        )

      case 'FILE':
        return (
          <>
            {/* Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={config.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
              />
            </Grid>

            {/* Variable (must be Object type) */}
            <Grid item xs={12}>
              <VariableAutocompleteWithCreate
                options={allVariables.filter(v => v.valueType === 'object')}
                valueInternalId={config.fileVariableName || ''}
                onChangeInternalId={(internalId) => handleChange('fileVariableName', internalId)}
                label="Variable"
                helperText="Object variable to read/write the file"
                required
                onCreateVariable={onCreateVariable}
                createDialogTitle="Create File Variable"
                createDialogDescription="Create an Object variable for file/photo data."
                showScopeSelector
                defaultScope="local"
                defaultValueType="object"
                showValueTypeSelector={false}
                groupBy={(option) => option.isGlobal ? 'Global Variables' : 'Local Variables'}
                getOptionLabel={(option) => {
                  const suffix = option.objectCode ? ` (${option.objectCode})` : ''
                  return `${option.internalId}${suffix}`
                }}
              />
            </Grid>

            {/* File Types */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>File Types</InputLabel>
                <Select
                  multiple
                  value={config.fileTypes || []}
                  onChange={(e) => handleChange('fileTypes', e.target.value as string[])}
                  label="File Types"
                  renderValue={(selected) => (selected as string[]).join(', ')}
                >
                  <MenuItem value="image">Image (Photo)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Editable */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!config.readOnly}
                    onChange={(e) => handleChange('readOnly', !e.target.checked)}
                  />
                }
                label="Editable (allow picking a new file)"
              />
            </Grid>
          </>
        )

      default:
        return null
    }
  }

  const handleSaveEmbeddedListConfig = (embeddedListConfig: {
    embeddedListItemId?: string
    embeddedListConfig?: Partial<MobileListItem>
    selectedIdsVariable?: string
    showCheckboxes?: boolean
    itemIdField?: string
    contextMenuActions?: ContextMenuAction[]
    listHeader?: string
    listSubheader?: string
    listSubheaderJs?: string
    selectAllEnabled?: boolean
    selectAllText?: string
    variableBindings?: Record<string, string>
    readOnlyIfJs?: string
  }) => {
    setConfig({
      ...config,
      ...embeddedListConfig,
    })
    setShowEmbeddedListConfig(false)
  }

  const handleSaveTitleJs = (value: string) => {
    handleChange('titleJs', value)
    setShowTitleJsEditor(false)
  }

  const handleSaveVisibleIfJs = (value: string) => {
    handleChange('visibleIfJs', value)
    setShowVisibleIfJsEditor(false)
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {mode === 'create' ? 'Add' : 'Edit'} {config.type} Element
        </DialogTitle>
        <DialogContent>
          {showEventFlows && (
            <Box sx={{ mb: 2 }}>
              <Button
                size='small'
                startIcon={<EventAvailableOutlinedIcon />}
                onClick={() => setEventFlowsModalOpen(true)}
              >
                View event flows for this element
              </Button>
            </Box>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {config.type !== 'DIVIDER' && (
              <Grid item xs={12}>
                <InternalIdInput
                  value={config.internalId ?? ''}
                  onChange={(v) => handleChange('internalId', v)}
                  fullWidth
                  label="Internal ID"
                  helperText="Unique ID for references and event flows"
                />
              </Grid>
            )}
            {renderFieldsForType()}
            {config.type !== 'DIVIDER' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Visible if (JS)"
                  value={config.visibleIfJs || ''}
                  helperText="Show element only when JS returns true"
                  InputProps={{
                    onClick: () => setShowVisibleIfJsEditor(true),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small"><CodeIcon /></IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {showEventFlows && elementRef && onEventFlowsChange && (
        <EventFlowsForElementModal
          open={eventFlowsModalOpen}
          onClose={() => setEventFlowsModalOpen(false)}
          title={`Event flows for this ${elementRef.type === 'button' ? 'button' : elementRef.type === 'swipe' ? 'swipe' : elementRef.type === 'field' ? 'field' : 'embedded list'}`}
          eventFlows={eventFlows}
          elementRef={elementRef}
          formElements={formElements}
          onEventFlowsChange={onEventFlowsChange}
        />
      )}

      {/* ScriptValueDialog for Title JS (BUTTON / SWIPE) */}
      {showTitleJsEditor && (
        <ScriptValueDialog
          isShow
          value={config.titleJs || ''}
          title="Edit Title JavaScript"
          onClose={() => setShowTitleJsEditor(false)}
          onSave={handleSaveTitleJs}
          language="js"
          variables={titleJsScriptVariables}
          fieldsInfo={titleJsScriptFieldsInfo}
          examples={[
            {
              title: 'Dynamic button text',
              code: 'return `Save ${formData.name || "Form"}`',
            },
            {
              title: 'Conditional text',
              code: 'return formData.id ? "Update" : "Create"',
            },
            {
              title: 'With item data',
              code: 'return `Delete ${item.name}`',
            },
          ]}
        />
      )}

      {showVisibleIfJsEditor && (
        <ScriptValueDialog
          isShow
          value={config.visibleIfJs || ''}
          title="Edit Visible If JavaScript"
          onClose={() => setShowVisibleIfJsEditor(false)}
          onSave={handleSaveVisibleIfJs}
          language="js"
          variables={titleJsScriptVariables}
          fieldsInfo={titleJsScriptFieldsInfo}
          examples={[{ title: 'Show when item has field', code: 'return !!item.aircraft_ref' }, { title: 'Show when formData matches', code: 'return formData.status === "READY"' }]}
        />
      )}

      {showValueJsEditor && (
        <ScriptValueDialog
          isShow
          value={config.valueJs || ''}
          title="Edit Field Value JavaScript"
          onClose={() => setShowValueJsEditor(false)}
          onSave={handleSaveValueJs}
          language="js"
          variables={valueJsScriptVariables}
          fieldsInfo={variablesToFieldsInfo(valueJsScriptVariables)}
          examples={[
            {
              title: 'From form data',
              code: "return (formData.firstName ?? '') + ' ' + (formData.lastName ?? '')",
            },
            {
              title: 'From local variable',
              code: 'return localVariables.myVar ?? ""',
            },
            {
              title: 'From global variable',
              code: 'return globalVariables.appSetting ?? ""',
            },
            {
              title: 'From userData (current user)',
              code: 'return userData?.name ?? userData?.preferred_username ?? ""',
            },
            {
              title: 'Conditional value',
              code: 'return formData.status === "active" ? "Active" : "Inactive"',
            },
            {
              title: 'Date formatting',
              code: 'return formData.createdAt ? new Date(formData.createdAt).toLocaleDateString() : ""',
            },
          ]}
        />
      )}

      {showEmbeddedListConfig && (
        <EmbeddedListConfigDialog
          open={showEmbeddedListConfig}
          initialData={{
            embeddedListItemId: config.embeddedListItemId,
            embeddedListConfig: config.embeddedListConfig,
            selectedIdsVariable: config.selectedIdsVariable,
            showCheckboxes: config.showCheckboxes,
            itemIdField: config.itemIdField,
            contextMenuActions: config.contextMenuActions,
            listHeader: config.listHeader,
            listSubheader: config.listSubheader,
            listSubheaderJs: config.listSubheaderJs,
            selectAllEnabled: config.selectAllEnabled,
            selectAllText: config.selectAllText,
            variableBindings: config.variableBindings,
            readOnlyIfJs: config.readOnlyIfJs,
          }}
          variables={variables}
          onClose={() => setShowEmbeddedListConfig(false)}
          onSave={handleSaveEmbeddedListConfig}
          onCreateVariable={onCreateVariable}
        />
      )}
    </>
  )
}

